import React, { useEffect, useState } from "react";
import Modal from "react-modal"; // Install 'react-modal' if not already installed
import { FaTimes, FaEdit, FaSave } from "react-icons/fa"; // Importing the icons from react-icons
import { jwtDecode } from "jwt-decode";
import axios from "axios"; // Import axios for API calls

const OrderDetails = ({ isOpen, onClose, order }) => {
  const [totalAmount, setTotalAmount] = useState(0); // Default to 0 to avoid issues
  const [isEditing, setIsEditing] = useState(false);
  const [newStatus, setNewStatus] = useState(order?.orderStatus);
  const [userData, setUserData] = useState();
  const [loading, setLoading] = useState(true);
  const userId = order?.user;
  const deliveryCharges = 100; // Example delivery charges, adjust as needed
  const CODCharge = 100;
  const token = localStorage.getItem("STITCHD_ID");
  const decoded = jwtDecode(token);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "80%", // adjust as needed
      maxHeight: "80vh", // adjust as needed
      overflow: "auto",
      padding: "20px",
      borderRadius: "10px",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.75)",
      zIndex: 1000, // ensure it overlays other content
    },
  };

  // Function to convert price string to number
  const parsePrice = (priceStr) => {
    if (!priceStr) return 0;
    return Number(priceStr.replace(/,/g, "").trim());
  };

  // Calculate total price
  const totalMRP = order?.item?.reduce(
    (total, item) => total + parsePrice(item.productId.price) * item.quantity,
    0
  );

  useEffect(() => {
    let amount;
    amount = totalMRP + (totalMRP > 1999 ? 0 : deliveryCharges);
    setTotalAmount(amount);
  }, [totalMRP, deliveryCharges]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_URL}/user/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setUserData(response.data.user);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setLoading(false);
      }
    };

    if (userId && decoded.role === "ADMIN") {
      fetchUserData();
    }
  }, [userId]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleStatusChange = (event) => {
    setNewStatus(event.target.value);
  };

  const handleSaveClick = async () => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_URL}/order/${order._id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        // Update the order status in the state
        order.orderStatus = newStatus;
        setIsEditing(false);
        alert("Order status updated successfully.");
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      alert("There was an error updating the order status. Please try again.");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Order Details"
      style={customStyles}
    >
      <div className="font-twentieth-century">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Order Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <FaTimes size={24} />
          </button>
        </div>
        <div className="flex justify-between">
          <div className="flex flex-col mt-2">
            <p className="text-gray-600 text-sm">Order Placed On:</p>
            <p>{formatDate(order?.createdAt)}</p>
          </div>

          <div className="flex flex-col mt-2">
            <p className="text-gray-600 text-sm">Order Status:</p>
            {isEditing ? (
              <div className="flex items-center">
                <input
                  type="text"
                  value={newStatus}
                  onChange={handleStatusChange}
                  className="border p-1 rounded"
                />
                <button
                  onClick={handleSaveClick}
                  className="text-green-500 ml-2"
                >
                  <FaSave size={24} />
                </button>
              </div>
            ) : (
              <div className="flex items-center">
                <p className="text-green-500 text-right">
                  {order?.orderStatus}
                </p>
                {decoded.role === "ADMIN" && (
                  <button
                    onClick={handleEditClick}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none ml-2"
                  >
                    <FaEdit size={24} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-between">
          <div className="flex flex-col">
            <p className="text-gray-600 text-sm">Order ID:</p>
            <p>{order?._id.slice(-7)}</p>
          </div>
          <div className="flex flex-col mt-2">
            <p className="text-gray-600 text-sm">Payment Mode:</p>
            <p className="text-right">{order?.paymentType}</p>
          </div>
        </div>
        <hr className="my-4" />
        <p className="font-semibold text-xl mb-2">ITEMS DETAILS</p>
        {order?.item?.map((product, index) => (
          <div key={index} className="flex items-center mb-2">
            <img
              src={`${process.env.REACT_APP_URL}/${product.productId.images[0]}`}
              alt={product.productId.name}
              className="w-16 h-16 object-cover rounded-md"
            />
            <div className="ml-2 flex-grow">
              <div className="flex justify-between items-center">
                <h4 className="md:text-lg text-sm md:font-medium font-semibold">
                  {product.productId.name}
                </h4>
                <p className="text-gray-600 px-2">₹{product.productId.price}</p>
              </div>
              <p className="text-gray-600">Color: {product.productId.color}</p>
              <p className="text-gray-600">
                Size: {product.size} | Quantity: {product.quantity}
              </p>
            </div>
          </div>
        ))}

        <hr className="my-4" />
        <div>
          <p className="font-semibold text-xl mb-2">SUMMARY</p>
          <div className="flex justify-between">
            <span className="w-20">Item Total:</span>
            <span className="md:ml-48">₹{totalMRP}</span>
          </div>
          <div>
            <div className="flex justify-between">
              <span>Delivery Charges:</span>
              <div>
                {totalMRP < 2000 ? (
                  <span>₹{deliveryCharges.toFixed(2)}</span>
                ) : (
                  <div className="flex">
                    <p className="line-through">
                      ₹{deliveryCharges.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between text-green-500">
              <p>Coupon Discount:</p>
              <p>
                - ₹
                {order?.paymentType === "COD"
                  ? totalAmount - order?.orderValue + CODCharge
                  : totalAmount - order?.orderValue}
              </p>
            </div>
            {order?.paymentType === "COD" && (
              <div className="flex justify-between text-red-500">
                <p>Logistic Charges:</p>
                <p>₹{CODCharge}</p>
              </div>
            )}
            <div className="flex justify-between font-semibold pt-2">
              <p>Total Amount:</p>
              <p>₹{order?.orderValue}</p>
            </div>
          </div>
        </div>
        <hr className="my-4" />
        <div className="md:flex">
          <p className="font-semibold md:text-md">Shipping Address :</p>
          <p className="mt-px md:ml-1"> {order?.address}</p>
        </div>
        {decoded.role === "ADMIN" && (
          <div>
            <hr className="my-4" />
            <div className="flex">
              <p className="font-semibold md:text-md">Order By Name :</p>
              <p className="mt-px md:ml-1"> {order?.name}</p>
            </div>
            <div className="flex">
              <p className="font-semibold md:text-md">Order By Number :</p>
              <p className="mt-px md:ml-1"> {order?.phone}</p>
            </div>
            {userData && (
              <div>
                <div className="flex">
                  <p className="font-semibold md:text-md">User Name :</p>
                  <p className="mt-px md:ml-1 text-orange-600">{userData.fullName}</p>
                </div>
                <div className="flex">
                  <p className="font-semibold md:text-md">User Number :</p>
                  <p className="mt-px md:ml-1 text-orange-600"> {userData.phoneNumber}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default OrderDetails;
